import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import SectionHeader from "./SectionHeader";
import Drawer from "./Drawer";

// Locked 16-track metadata. Extended fields transcribed verbatim from the
// original 2003 booklet scans (album-booklet-01..10). Where the booklet
// omitted a specific credit it is left absent — no hallucination.
const TRACKS = [
  { n:"01", title:"Free",
    writtenBy:"L. Pridgen, J. Smith, J. Melotti, V. Robinson (BMI/ASCAP)",
    producedBy:'Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World',
    recordedAt:'Baseline Studios, NYC by Gimel "Young Guru" Keaton for Loreal, Inc. and at Quad Recording, NYC by Carlisle Young for Blessed One Entertainment',
    mixedAt:'Quad Recording, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.',
    sample:'Contains a sample of "Freeway Song," written by J. Melotti and V. Robinson, published by Print Music Co., Inc. (ASCAP). Performed by Vicki Sue Robinson. Used courtesy of RCA Records.' },

  { n:"02", title:"What We Do", featuring:"JAY-Z and Beanie Sigel",
    writtenBy:"L. Pridgen, S. Smith, C. Carter, D. Grant, S. Scarborough (BMI/ASCAP)",
    producedBy:'Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World',
    recordedAt:'Baseline Studios, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.',
    mixedAt:'Right Track Recording, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.',
    courtesyOf:'Jay-Z appears courtesy of Roc-A-Fella Records. Beanie Sigel appears courtesy of Roc-A-Fella Records.',
    sample:'Contains a sample from "I Just Can\'t See Myself Without You," written by S. Scarborough, used courtesy of Unichappell Music, Inc. / Alexscar Music (BMI). Performed by Creative Source. Used under license from Universal Music Enterprises, a division of UMG Recordings, Inc.' },

  { n:"03", title:"All My Life", featuring:"Nate Dogg",
    writtenBy:"L. Pridgen, R. Harrell, N. Hale, C. Mayfield, B. Krass (BMI/SESAC)",
    producedBy:"Bink! for One Shot Deal Inc. / Loreal, Inc.",
    recordedAt:'Circle House Studios, Miami, FL by Doug Wilson for Loreal, Inc.; Sound On Sound, NYC by Brian Stanley for Team B / Loreal, Inc.; and at Quad Recording, NYC by Carlisle Young for Blessed One Entertainment',
    mixedAt:'Sound On Sound, NYC by Brian Stanley for Team B / Loreal, Inc.',
    courtesyOf:'Nate Dogg appears courtesy of Elektra Records.',
    sample:'Contains a sample of "I Want To Go Back" (aka "Man Oh Man"), written by Curtis Mayfield and Ben Krass, published by Chi-Sound Music (BMI). All rights on behalf of Chi-Sound Music (BMI) administered by Warner-Tamerlane Publishing Corp. (BMI). Performed by The Impressions. Used under license from Universal Music Enterprises, a division of UMG Recordings, Inc.' },

  { n:"04", title:"Flipside", featuring:"Peedi Crakk",
    writtenBy:"L. Pridgen, J. Smith, P. Zayas (BMI/ASCAP/Copyright Control)",
    producedBy:"Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World",
    recordedAt:'Baseline Studios, NYC and Sound On Sound, NYC by Gimel "Young Guru" Keaton for Loreal, Inc., and at Quad Recording, NYC by Carlisle Young for Blessed One Entertainment',
    mixedAt:'Sound On Sound, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.' },

  { n:"05", title:"On My Own", featuring:"Nelly",
    writtenBy:"L. Pridgen, J. Smith, Nelly (BMI/ASCAP)",
    producedBy:"Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World",
    recordedAt:'Baseline Studios, NYC and at Quad Recording, NYC by Carlisle Young for Blessed One Entertainment',
    mixedAt:'Sound On Sound, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.',
    courtesyOf:'Nelly appears courtesy of Universal Records.' },

  { n:"06", title:"We Get Around", featuring:"Snoop Dogg",
    writtenBy:"L. Pridgen, C. Broadus, J. Smith (BMI/ASCAP)",
    producedBy:"Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World",
    recordedAt:'Baseline Studios, NYC by Shane "Bermuda" Woodley for Blessed One Entertainment',
    mixedAt:'Sound On Sound, NYC and at Quad Recording, NYC by Doug Wilson for Loreal, Inc.',
    courtesyOf:'Snoop Dogg appears courtesy of Priority Records.' },

  { n:"07", title:"Don\u2019t Cross the Line", featuring:"Faith Evans",
    writtenBy:"L. Pridgen, J. Smith, V. Barrett, B. Eli (BMI/ASCAP)",
    producedBy:"Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World",
    recordedAt:'Baseline Studios, NYC by Gimel "Young Guru" Keaton for Loreal, Inc. and Shane "Bermuda" Woodley for Blessed One Entertainment',
    mixedAt:'Quad Recording, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.',
    courtesyOf:'Faith Evans appears courtesy of Arista / Bad Boy Records.',
    sample:'Contains a sample of "To Get Love You Must Give Love," written by V. Barrett and B. Eli, used courtesy of Warner-Tamerlane Publishing Corp. (BMI). Performed by Ultimate Music Experience.' },

  { n:"08", title:"Life", featuring:"Beanie Sigel",
    writtenBy:"L. Pridgen, M. Cartel, D. Grant, E. Money (BMI/ASCAP)",
    producedBy:"Ruggedness for PB Productions. Co-Produced by E-Bass for E-Bass Musik / N.Q.C. Management.",
    recordedAt:"Baseline Studios, NYC",
    mixedAt:'Sound On Sound, NYC by Pat Viala for Pat \u2019Em Down, Inc. / Loreal, Inc. Mixed by Gimel "Young Guru" Keaton for Loreal, Inc.',
    additionalMusicians:'Guitar and Bass played by E-Bass for E-Bass Musik / N.Q.C. Management. Additional vocals by M.A.J.',
    courtesyOf:'Beanie Sigel appears courtesy of Roc-A-Fella Records.',
    interpolation:'Contains an interpolation of "Life For The Taking," written by Eddie Money, used courtesy of Three Wise Boys Music LLC / Embassy Music Corp. (BMI).' },

  { n:"09", title:"Full Effect", featuring:"Young Gunz",
    writtenBy:"L. Pridgen, J. Smith, C. Rios, H. Mohammad (BMI/ASCAP/Copyright Control)",
    producedBy:"Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World",
    recordedAt:'Baseline Studios, NYC by Shane "Bermuda" Woodley for Blessed One Entertainment, and at Quad Recording, NYC by Carlisle Young for Blessed One Entertainment',
    mixedAt:'Battery Studios, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.' },

  { n:"10", title:"Turn Out the Lights (Freewest)", featuring:"Kanye West",
    writtenBy:"L. Pridgen, K. West (BMI/ASCAP)",
    producedBy:"Kanye West for Konman Productions / Roc The World",
    recordedAt:'Baseline Studios, NYC by Shane "Bermuda" Woodley for Blessed One Entertainment, and at Larrabee Sound North, Los Angeles, CA by Rabeka Tunei',
    mixedAt:"Larrabee Sound North, Los Angeles, CA by Manny Marroquin" },

  { n:"11", title:"Victim of the Ghetto", featuring:"Rell",
    writtenBy:"L. Pridgen, R. Harrell, Y. Davis (BMI/SESAC)",
    producedBy:"Bink! for One Shot Deal Inc. / Loreal, Inc.",
    recordedAt:'Baseline Studios, NYC by Gimel "Young Guru" Keaton for Loreal, Inc. and at Sound On Sound, NYC by Doug Wilson for Loreal, Inc.',
    mixedAt:"Sound On Sound, NYC by Doug Wilson for Loreal, Inc.",
    sample:'Contains a sample of "We Belong Together," written by Y. Davis, used courtesy of Warner-Tamerlane Publishing Corp. (BMI). Performed by The Spinners. Produced under license from Atlantic Recording Corp. by arrangement with Warner Special Products.' },

  { n:"12", title:"You Don\u2019t Know (In the Ghetto)", featuring:"Omillio Sparks",
    writtenBy:"L. Pridgen, M. Davis, R. Riggerton, R. Rudolph, L. Ware (BMI/ASCAP)",
    producedBy:"Black Key for Black Key Productions, Inc.",
    recordedAt:'Baseline Studios, NYC by Shane "Bermuda" Woodley for Blessed One Entertainment and at Quad Recording, NYC by Carlisle Young for Blessed One Entertainment',
    mixedAt:'Sony Music Studios, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.',
    additionalMusicians:"Additional vocals by Alaine Houghton.",
    courtesyOf:"Sparks appears courtesy of Roc-A-Fella Records.",
    sample:'Contains a sample of "Inside My Love," written by M. Ripperton, R. Rudolph and L. Ware, published by Jobete Music Co., Inc. (ASCAP) / Dickiebird Music / Embassy Music Corp. (BMI). Performed by Minnie Riperton. Used under license from Capitol Records.' },

  { n:"13", title:"Alright", featuring:"Allen Anthony",
    writtenBy:"L. Pridgen, A. Anthony, J. Smith, R. Foster (BMI/ASCAP)",
    producedBy:"Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World. Additional production by Gee Roberson.",
    recordedAt:'Baseline Studios, NYC by Shane "Bermuda" Woodley',
    mixedAt:'Quad Recording, NYC by Gimel "Young Guru" Keaton for Loreal, Inc.',
    interpolation:'Contains an interpolation of "Mystic Brew," written by R. Foster, published by EMI Unart Catalog, Inc. (BMI).' },

  { n:"14", title:"Hear the Song",
    writtenBy:"L. Pridgen, K. West (BMI/ASCAP)",
    producedBy:"Kanye West for Konman Productions / Roc The World",
    recordedAt:'Baseline Studios, NYC by Shane "Bermuda" Woodley for Blessed One Entertainment; Larrabee Sound North, Los Angeles, CA by Rabeka Tunei; and at Quad Studios, NYC by Carlisle Young for Blessed One Entertainment',
    mixedAt:"Larrabee Sound North, Los Angeles, CA by Manny Marroquin",
    additionalMusicians:"Additional vocals by Torrey Toraz." },

  { n:"15", title:"You Got Me", featuring:"Mariah Carey, Freeway and JAY-Z",
    writtenBy:"M. Carey, S. Carter, L. Pridgen, J. Smith (BMI/ASCAP)",
    producedBy:"Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World and Mariah Carey for MonarC Entertainment",
    recordedAt:"Engineered by Dana Jon Chappelle and Paul Gregory. Assistant Engineers: Manuel Farolfi, Giulio Antognini \u2014 Capri Studio, Capri; Dave Perini \u2014 Right Track Studios, NYC; Colin Miller \u2014 Studio Atlantis, CA.",
    mixedAt:"Studio Atlantis, CA by Ken Lewis for N.Q.C. Management, LLC",
    additionalMusicians:"Background vocals: Mariah Carey. Rap: Jay-Z. All instruments played by Just Blaze.",
    courtesyOf:"Mariah Carey appears courtesy of MonarC / Island Records. Jay-Z appears courtesy of Roc-A-Fella Records, LLC." },

  { n:"16", title:"Line \u2019Em Up", featuring:"Young Chris",
    writtenBy:"L. Pridgen, J. Smith, C. Riles (BMI/ASCAP/Copyright Control)",
    producedBy:"Just Blaze for F.O.B. Entertainment / N.Q.C. Management / Roc The World",
    recordedAt:'Baseline Studios, NYC by Gimel "Young Guru" Keaton for Loreal Inc.',
    mixedAt:'Battery Studios, NYC by Gimel "Young Guru" Keaton for Loreal Inc.' },
];

function MetaRow({ label, value, dark = true }) {
  return (
    <div>
      <div className={`font-mono text-[9px] tracking-[0.35em] uppercase mb-1 ${dark ? "text-[#F4C10A]" : "text-[#B89326]"}`}>{label}</div>
      <div className={`font-body text-sm ${dark ? "text-white/85" : "text-black/85"}`}>{value}</div>
    </div>
  );
}

export default function Tracklist() {
  const [openTrack, setOpenTrack] = useState(null);
  return (
    <section id="tracklist" data-testid="tracklist-section" className="relative bg-[#0A0A0A] text-white overflow-hidden">
      <div className="absolute inset-0 grain pointer-events-none" />
      <SectionHeader src="/assets/headers/tracklist.jpg" alt="Track Listing" fadeTo="black" />
      <div className="relative px-4 md:px-10 max-w-[1400px] mx-auto pb-20 md:pb-28 -mt-10">
        <div className="mb-8 max-w-4xl relative z-10">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F4C10A] mb-3">
            Original Album · February 25, 2003
          </div>
          <h2
            data-testid="tracklist-heading"
            className="font-wide text-white uppercase text-4xl sm:text-5xl md:text-6xl"
          >
            Philadelphia Freeway Tracklist
          </h2>
          <p className="mt-5 font-body text-white/70 text-base md:text-lg leading-relaxed">
            Philadelphia Freeway is the debut solo studio album by American rapper Freeway. It was released on February 25, 2003, via Roc-A-Fella Records. Recording sessions took place at Baseline Studios, Quad Recording Studios and Sound On Sound in New York, Circle House Studios in Miami and Larrabee North in Los Angeles.
          </p>
        </div>

        <Drawer id="tracklist">
          <ul data-testid="tracklist" className="space-y-1">
            {TRACKS.map((t) => {
              const open = openTrack === t.n;
              return (
                <li key={t.n} className="bg-[#F4C10A]">
                  <button
                    data-testid={`track-${t.n}`}
                    onClick={() => setOpenTrack(open ? null : t.n)}
                    className="w-full flex items-center gap-4 md:gap-6 px-4 md:px-6 py-4 text-left text-black border-b border-black/25 hover:bg-[#FFD22C] transition-colors"
                  >
                    <span className="font-mono text-xs w-8 flex-shrink-0">{t.n}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-wide text-lg md:text-xl uppercase text-black">
                        {t.title}
                      </div>
                      {t.featuring && (
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-black/70 mt-1">
                          Ft. {t.featuring}
                        </div>
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-black transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden bg-black text-white"
                      >
                        <div className="px-6 md:px-10 py-6 space-y-4 max-w-3xl">
                          {t.featuring && <MetaRow label="Featuring" value={t.featuring} />}
                          {t.producedBy && <MetaRow label="Produced By" value={t.producedBy} />}
                          {t.writtenBy && <MetaRow label="Written By" value={t.writtenBy} />}
                          {t.recordedAt && <MetaRow label="Recorded At" value={t.recordedAt} />}
                          {t.mixedAt && <MetaRow label="Mixed At" value={t.mixedAt} />}
                          {t.additionalMusicians && <MetaRow label="Additional Musicians" value={t.additionalMusicians} />}
                          {t.courtesyOf && <MetaRow label="Courtesy Of" value={t.courtesyOf} />}
                          {t.sample && <MetaRow label="Sample" value={t.sample} />}
                          {t.interpolation && <MetaRow label="Interpolation" value={t.interpolation} />}
                          {t.audioSrc && (
                            <audio
                              data-testid={`track-audio-${t.n}`}
                              controls
                              preload="none"
                              src={t.audioSrc}
                              className="w-full mt-2"
                            />
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </Drawer>
      </div>
    </section>
  );
}
